// @ts-nocheck
// Call Stats — every chart on the page as a small, self-contained
// component: each owns its own chart config from raw data props instead of
// the parent computing every chart's options/series.
//
import React, { useMemo } from 'react';
import moment from 'moment';
import Chart from 'react-apexcharts';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getChartColors } from './colors';
import { formatDuration, formatDurationShort } from './format';

export const CHART_HEIGHT = 260;

const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };
const TOOLTIP_STYLE = {
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 10,
  fontSize: 12,
  color: 'hsl(var(--foreground))',
};
const TOOLTIP_LABEL_STYLE = { color: 'hsl(var(--foreground))', fontWeight: 600, marginBottom: 4 };

function EmptyChart({ label }: any) {
  return (
    <div
      className="flex items-center justify-center text-[12px] text-muted-foreground"
      style={{ height: CHART_HEIGHT }}
    >
      {label}
    </div>
  );
}

function useApexBase(dailyFilled, isDark) {
  return useMemo(() => {
    const c = getChartColors(isDark);
    return {
      chart: { toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent' },
      grid: { borderColor: c.grid, strokeDashArray: 4 },
      legend: { labels: { colors: c.axis } },
      tooltip: { theme: isDark ? 'dark' : 'light' },
      xaxis: {
        categories: dailyFilled.map((d) => moment(d.date).format('DD MMM')),
        labels: { style: { colors: c.axis, fontSize: '11px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: c.axis, fontSize: '11px' } } },
    };
  }, [dailyFilled, isDark]);
}

export function VolumeTrendChart({ dailyFilled, isDark }: any) {
  const c = getChartColors(isDark);
  const series = useMemo(
    () => dailyFilled.map((d) => ({ date: moment(d.date).format('DD MMM'), incoming: d.incoming, outgoing: d.outgoing })),
    [dailyFilled]
  );
  if (!dailyFilled.some((d) => d.total > 0)) return <EmptyChart label="No call volume for selected range" />;
  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart data={series} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
        <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="hsl(var(--border))" />
        <XAxis dataKey="date" axisLine={false} tickLine={false} tickMargin={8} tick={AXIS_TICK} />
        <YAxis axisLine={false} tickLine={false} width={32} allowDecimals={false} tick={AXIS_TICK} />
        <Tooltip
          cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
        />
        <Bar dataKey="incoming" name="Incoming" fill={c.direction[0]} radius={[3, 3, 0, 0]} barSize={16} />
        <Bar dataKey="outgoing" name="Outgoing" fill={c.direction[1]} radius={[3, 3, 0, 0]} barSize={16} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function TalkTimeTrendChart({ dailyFilled, isDark }: any) {
  const c = getChartColors(isDark);
  const series = useMemo(
    () => dailyFilled.map((d) => ({ date: moment(d.date).format('DD MMM'), talkTime: Number(d.talk_time) || 0 })),
    [dailyFilled]
  );
  const average = useMemo(() => {
    if (!series.length) return 0;
    return series.reduce((sum, d) => sum + d.talkTime, 0) / series.length;
  }, [series]);

  if (!dailyFilled.some((d) => d.talk_time > 0)) {
    return <EmptyChart label="No talk time recorded for selected range" />;
  }

  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <AreaChart data={series} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
        <defs>
          <linearGradient id={`cs-talktime-fill-${isDark ? 'dark' : 'light'}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c.hex.call} stopOpacity={0.35} />
            <stop offset="100%" stopColor={c.hex.call} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="hsl(var(--border))" />
        <XAxis dataKey="date" axisLine={false} tickLine={false} tickMargin={8} tick={AXIS_TICK} />
        <YAxis
          axisLine={false}
          tickLine={false}
          width={44}
          tick={AXIS_TICK}
          tickFormatter={(v) => formatDurationShort(v)}
        />
        <Tooltip
          cursor={{ stroke: 'hsl(var(--border))' }}
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          formatter={(v) => [formatDuration(v), 'Talk time']}
        />
        {average > 0 ? (
          <ReferenceLine
            y={average}
            stroke="hsl(var(--muted-foreground))"
            strokeDasharray="4 4"
            strokeOpacity={0.7}
            label={{
              value: `avg ${formatDurationShort(average)}`,
              position: 'insideTopRight',
              fill: 'hsl(var(--muted-foreground))',
              fontSize: 10,
            }}
          />
        ) : null}
        <Area
          type="monotone"
          dataKey="talkTime"
          name="Talk time"
          stroke={c.hex.call}
          strokeWidth={2}
          fill={`url(#cs-talktime-fill-${isDark ? 'dark' : 'light'})`}
          // An area/line needs 2+ points to draw any visible path — with a
          // single-day range (e.g. "Today") there's nothing to connect, so
          // with dot={false} the whole chart rendered blank even though
          // valid non-zero data existed. Show a single point instead of
          // nothing when there's only one day in range.
          dot={series.length < 2 ? { r: 4, strokeWidth: 0, fill: c.hex.call } : false}
          activeDot={{ r: 3.5, strokeWidth: 0, fill: c.hex.call }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// Hand-drawn labeled-segment ring — same design as PeriodOverviewCard's
// Today/Yesterday/This-Week rings, generalized to N segments so it also
// covers this donut's three outcomes. Each segment's own % badge sits on
// the arc instead of relying on ApexCharts' legend + data-label combo.
const OUTCOME_RING_SIZE = 220;
const OUTCOME_RING_R = 76;
const OUTCOME_RING_STROKE = 24;
const OUTCOME_RING_CIRC = 2 * Math.PI * OUTCOME_RING_R;

function outcomeRingPoint(fraction, radius) {
  const angle = fraction * 2 * Math.PI;
  const center = OUTCOME_RING_SIZE / 2;
  return { x: center + radius * Math.sin(angle), y: center - radius * Math.cos(angle) };
}

export function OutcomeDistributionDonut({ totals, isDark }: any) {
  const total = Number(totals.total) || 0;
  if (total <= 0) return <EmptyChart label="No outcomes to display" />;

  const c = getChartColors(isDark);
  const segments = useMemo(() => {
    const defs = [
      { key: 'answered', label: 'Answered', value: Number(totals.answered) || 0, color: c.hex.good },
      { key: 'failed', label: 'Failed', value: Number(totals.failed) || 0, color: c.hex.crit },
      { key: 'abandoned', label: 'Abandoned', value: Number(totals.abandoned) || 0, color: c.hex.warn },
    ];
    let cursor = 0;
    return defs.map((seg) => {
      const fraction = seg.value / total;
      const start = cursor;
      cursor += fraction;
      return { ...seg, fraction, start };
    });
  }, [totals.answered, totals.failed, totals.abandoned, total, c.hex]);

  return (
    <div
      className="flex flex-col items-center justify-center gap-4"
      style={{ minHeight: CHART_HEIGHT }}
    >
      <div className="relative shrink-0" style={{ width: OUTCOME_RING_SIZE, height: OUTCOME_RING_SIZE }}>
        <svg width={OUTCOME_RING_SIZE} height={OUTCOME_RING_SIZE} viewBox={`0 0 ${OUTCOME_RING_SIZE} ${OUTCOME_RING_SIZE}`}>
          {segments.map((seg) =>
            seg.fraction > 0 ? (
              <circle
                key={seg.key}
                cx={OUTCOME_RING_SIZE / 2}
                cy={OUTCOME_RING_SIZE / 2}
                r={OUTCOME_RING_R}
                fill="none"
                stroke={seg.color}
                strokeWidth={OUTCOME_RING_STROKE}
                strokeDasharray={`${seg.fraction * OUTCOME_RING_CIRC} ${OUTCOME_RING_CIRC}`}
                strokeDashoffset={-seg.start * OUTCOME_RING_CIRC}
                transform={`rotate(-90 ${OUTCOME_RING_SIZE / 2} ${OUTCOME_RING_SIZE / 2})`}
              />
            ) : null,
          )}
          <text x={OUTCOME_RING_SIZE / 2} y={OUTCOME_RING_SIZE / 2 - 8} textAnchor="middle" fontSize="13" fill={c.axis}>
            Total
          </text>
          <text
            x={OUTCOME_RING_SIZE / 2}
            y={OUTCOME_RING_SIZE / 2 + 20}
            textAnchor="middle"
            fontSize="30"
            fontWeight="700"
            fill={c.hex.call}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {total}
          </text>
        </svg>
        {segments.map((seg) => {
          if (seg.fraction <= 0) return null;
          const pos = outcomeRingPoint(seg.start + seg.fraction / 2, OUTCOME_RING_R);
          const pct = Math.round(seg.fraction * 1000) / 10;
          return (
            <span
              key={seg.key}
              className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-bold text-white shadow-sm"
              style={{ left: pos.x, top: pos.y, background: seg.color }}
            >
              {pct}%
            </span>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
        {segments.map((seg) => (
          <span key={seg.key} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: seg.color }} />
            {seg.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function DailyOutcomesBar({ dailyFilled, isDark }: any) {
  const base = useApexBase(dailyFilled, isDark);
  const options: any = useMemo(
    () => {
      const c = getChartColors(isDark);
      // Same [Answered, Failed, Abandoned] palette as OutcomeDistributionDonut.
      return {
        ...base,
        chart: { ...base.chart, type: 'bar', stacked: true },
        plotOptions: { bar: { borderRadius: 6, columnWidth: '55%' } },
        colors: [c.hex.good, c.hex.crit, c.hex.warn],
        dataLabels: { enabled: false },
      };
    },
    [base, isDark]
  );
  const series = useMemo(
    () => [
      { name: 'Answered', data: dailyFilled.map((d) => d.answered) },
      { name: 'Failed', data: dailyFilled.map((d) => d.failed) },
      { name: 'Abandoned', data: dailyFilled.map((d) => d.abandoned) },
    ],
    [dailyFilled]
  );
  if (!dailyFilled.some((d) => d.total > 0)) return <EmptyChart label="No daily data for selected range" />;
  return <Chart options={options} series={series} type="bar" height={CHART_HEIGHT} />;
}
